import React, { useState } from "react";
import { Box, Button, Stack, TextField, Typography, Alert } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { grey } from "@mui/material/colors";
import { ArrowBack, ArrowForwardIos } from "@mui/icons-material";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../../config/firebase";
import { toast } from "react-toastify";
import background from "../../assets/rome-background.png";
import { useTheme } from "@mui/material/styles";
import { MiniHeader } from "../../components/header/MiniHeader";

export const ForgotPasswordPage = () => {
    const [email, setEmail] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isEmailSent, setIsEmailSent] = useState(false);
    const navigate = useNavigate();
    const theme = useTheme();

    const handleResetPassword = async () => {
        if (!email) {
            toast.error("Please enter your email address");
            return;
        }

        setIsLoading(true);
        
        try {
            await sendPasswordResetEmail(auth, email);
            setIsEmailSent(true);
            toast.success("Password reset email sent! Check your inbox.");
        } catch (err) {
            console.error(err);
            
            // Handle specific error cases
            switch (err.code) {
                case 'auth/user-not-found':
                    toast.error("No account found with this email address");
                    break;
                case 'auth/invalid-email':
                    toast.error("Please enter a valid email address");
                    break;
                case 'auth/too-many-requests':
                    toast.error("Too many requests. Please try again later");
                    break;
                default:
                    toast.error("Failed to send password reset email");
                    break;
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleResetPassword();
        }
    };

    return (
        <Box sx={{ backgroundImage: `url(${background})`, backgroundSize: 'cover', minHeight: '100vh' }}>
            <MiniHeader />

            <Stack direction={"column"} sx={{ width: "100%", height: "100vh" }} justifyContent={'center'} alignItems={"center"}>
                <Box sx={{
                    bgcolor: "#FFF",
                    border: 1,
                    borderRadius: 5,
                    borderColor: "divider",
                    p: 5,
                    boxShadow: 3,
                    minWidth: "300px",
                    maxWidth: "450px"
                }}>
                    <Stack direction={"column"} gap={3} sx={{ width: "100%", height: "100%" }} justifyContent={'center'} alignItems={"center"}>
                        {/* HEADER */}
                        <Stack direction={"column"} gap={2} sx={{ width: '100%' }}>
                            {/* BACK TO LOGIN */}
                            <Button
                                component={Link}
                                to={"/login"}
                                startIcon={<ArrowBack />}
                                sx={{
                                    color: theme.palette.purple.main,
                                    textTransform: "none",
                                    alignSelf: "flex-start",
                                    p: 0,
                                    minWidth: "auto"
                                }}
                            >
                                Back to Login
                            </Button>
                            
                            {/* TITLE */}
                            <Typography sx={{ color: theme.palette.purple.main }} variant={'h4'} textAlign={"center"} fontWeight={"bold"}>
                                Reset Password
                            </Typography>
                            
                            {/* DESCRIPTION */}
                            <Typography sx={{ color: grey[700] }} textAlign={"center"}>
                                {isEmailSent 
                                    ? "We've sent you a password reset link. Check your email and follow the instructions to reset your password."
                                    : "Enter your email address and we'll send you a link to reset your password."
                                }
                            </Typography>
                        </Stack>

                        {!isEmailSent ? (
                            <>
                                {/* EMAIL INPUT */}
                                <Stack direction={"column"} gap={2} sx={{ width: '100%' }}>
                                    <TextField
                                        type={"email"}
                                        variant={"standard"}
                                        required
                                        fullWidth
                                        label={"Email"}
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        placeholder={"Enter your email address"}
                                        autoComplete="email"
                                        name="email"
                                        id="email"
                                        autoFocus
                                        disabled={isLoading}
                                    />
                                </Stack>

                                {/* RESET BUTTON */}
                                <Button
                                    fullWidth
                                    variant={"contained"}
                                    onClick={handleResetPassword}
                                    disabled={!email || isLoading}
                                    sx={{
                                        textTransform: "capitalize",
                                        bgcolor: theme.palette.purple.main,
                                        fontWeight: "bold",
                                        '&:hover': { bgcolor: theme.palette.purple.dark }
                                    }}
                                >
                                    {isLoading ? "Sending..." : "Send Reset Email"} <ArrowForwardIos sx={{ fontSize: 12 }} />
                                </Button>
                            </>
                        ) : (
                            <>
                                {/* SUCCESS MESSAGE */}
                                <Alert severity="success" sx={{ width: "100%" }}>
                                    Password reset email sent successfully!
                                </Alert>
                                
                                {/* ACTIONS */}
                                <Stack direction={"column"} gap={2} sx={{ width: '100%' }}>
                                    <Button
                                        fullWidth
                                        variant={"contained"}
                                        component={Link}
                                        to={"/login"}
                                        sx={{
                                            textTransform: "capitalize",
                                            bgcolor: theme.palette.purple.main,
                                            fontWeight: "bold",
                                            '&:hover': { bgcolor: theme.palette.purple.dark }
                                        }}
                                    >
                                        Back to Login
                                    </Button>
                                    
                                    <Button
                                        fullWidth
                                        variant={"outlined"}
                                        onClick={() => {
                                            setIsEmailSent(false);
                                            setEmail("");
                                        }}
                                        sx={{
                                            textTransform: "capitalize",
                                            color: theme.palette.purple.main,
                                            borderColor: theme.palette.purple.main,
                                            '&:hover': { 
                                                borderColor: theme.palette.purple.dark,
                                                color: theme.palette.purple.dark
                                            }
                                        }}
                                    >
                                        Send to Different Email
                                    </Button>
                                </Stack>
                            </>
                        )}
                    </Stack>
                </Box>
            </Stack>
        </Box>
    );
}; 